import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Instagram, MapPin, Star, Heart, Search, Download, RefreshCw } from "lucide-react";

// P2-6: Properly escape CSV fields — handles commas, quotes, and newlines
function csvEsc(v) {
  const s = String(v ?? "").replace(/\r?\n/g, " ").replace(/"/g, '""');
  return `"${s}"`;
}

function exportCsv(leads) {
  const headers = ["Nome", "Telefone", "Email", "Visitas", "Consentimento", "Instagram", "CheckIn", "Google", "Última visita"];
  const rows = leads.map((l) => [
    csvEsc(l.name),
    csvEsc(l.phone),
    csvEsc(l.email || ""),
    csvEsc(l.visits_count),
    csvEsc(l.marketing_consent ? "sim" : "não"),
    csvEsc(l.instagram_clicked ? "sim" : "não"),
    csvEsc(l.checkin_clicked ? "sim" : "não"),
    csvEsc(l.google_review_clicked ? "sim" : "não"),
    csvEsc(l.last_visit_at ? l.last_visit_at.slice(0, 10) : ""),
  ].join(","));

  const csv = [headers.map(csvEsc).join(","), ...rows].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "wifi_leads.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function LeadRow({ lead }) {
  const date = lead.last_visit_at
    ? new Date(lead.last_visit_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })
    : "—";
  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="py-2 px-3 font-medium">{lead.name}</td>
      <td className="py-2 px-3 text-sm text-gray-600">{lead.phone}</td>
      <td className="py-2 px-3 text-sm text-gray-500">{lead.email || "—"}</td>
      <td className="py-2 px-3 text-center">
        <Badge variant={lead.visits_count > 1 ? "default" : "secondary"}>{lead.visits_count}</Badge>
      </td>
      <td className="py-2 px-3">
        <div className="flex gap-1">
          {lead.marketing_consent && <Heart className="h-4 w-4 text-red-400" title="GDPR" />}
          {lead.instagram_clicked && <Instagram className="h-4 w-4 text-pink-500" title="Instagram" />}
          {lead.checkin_clicked && <MapPin className="h-4 w-4 text-orange-500" title="Check-in" />}
          {lead.google_review_clicked && <Star className="h-4 w-4 text-yellow-500" title="Google" />}
        </div>
      </td>
      <td className="py-2 px-3 text-sm text-gray-500">{date}</td>
    </tr>
  );
}

export default function WiFiLeadsTable({ leads = [], loading, onRefresh }) {
  const [search, setSearch] = useState("");

  const filtered = leads.filter(
    (l) =>
      l.name?.toLowerCase().includes(search.toLowerCase()) ||
      l.phone?.includes(search) ||
      l.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Pesquisar leads..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
        </div>
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => exportCsv(filtered)} disabled={!filtered.length}>
          <Download className="h-4 w-4 mr-1" /> Exportar CSV
        </Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-2 px-3 text-left font-medium text-gray-600">Nome</th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">Telefone</th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">Email</th>
              <th className="py-2 px-3 text-center font-medium text-gray-600">Visitas</th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">Interações</th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">Última visita</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-8 text-center text-gray-400">A carregar...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-gray-400">Nenhum lead encontrado</td></tr>
            )}
            {!loading && filtered.map((lead) => <LeadRow key={lead.id} lead={lead} />)}
          </tbody>
        </table>
      </div>
      {!loading && <p className="text-xs text-gray-400">{filtered.length} leads</p>}
    </div>
  );
}
